import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { CustomField, MailrailAutoSendEmailCampaign } from '@feathr/blackbox';
import { FieldCollection } from '@feathr/blackbox';
import { Value } from '@feathr/components';
import type { IAttrOption } from '@feathr/extender/components/PredicateList/Predicates/utils';
import { useStore } from '@feathr/extender/state';

import { getTriggerComparisonLabel, getTriggerValueLabel } from './MailrailTriggerSummary.utils';

import * as styles from './MailrailTriggerSummary.css';

import defaultBreadcrumbFields from '@feathr/blackbox/fixtures/defaultBreadcrumbFields.json';
import defaultPersonFields from '@feathr/blackbox/fixtures/defaultPersonFields.json';

interface IMailrailTriggerSummaryProps {
  /** A Mailrail autosend/drip campaign — reads its `predicates` enrollment trigger. */
  campaign: MailrailAutoSendEmailCampaign;
}

function customFieldToOption(field: CustomField): IAttrOption {
  return {
    id: `custom.${field.get('f_key')}`,
    name: field.get('u_key'),
    type: field.get('data_type'),
    kind: field.get('collection') as FieldCollection.Person | FieldCollection.Breadcrumb,
    helpText: field.get('description'),
  };
}

/**
 * Read-only summary of a Mailrail drip/autosend campaign's enrollment trigger. Renders one sentence
 * per predicate (field name + comparison + value), or `(None)` when there are no predicates. Renders
 * ONLY the predicate trigger list — trigger type and delay are surfaced separately by the preview.
 *
 * @param props - {@link IMailrailTriggerSummaryProps}.
 */
const MailrailTriggerSummary = observer(
  ({ campaign }: IMailrailTriggerSummaryProps): JSX.Element => {
    const { t } = useTranslation();
    const store = useStore();
    const { CustomFields } = store;
    const customFields = CustomFields.list({
      filters: { collection__in: [FieldCollection.Person, FieldCollection.Breadcrumb] },
      pagination: { page: 0, page_size: 1000 },
    });

    const allFields: IAttrOption[] = [
      ...defaultPersonFields,
      ...defaultBreadcrumbFields,
      ...customFields.models.map((field) => customFieldToOption(field)),
    ];

    const predicates = campaign.get('predicates') ?? [];

    if (predicates.length === 0) {
      return <>{t('(None)')}</>;
    }

    return (
      <ul className={styles.triggerList}>
        {predicates.map((trigger) => {
          const field = allFields.find(({ id }) => id === trigger.attr_against);
          const attrLabel = field?.name ?? '-';
          const comparison = getTriggerComparisonLabel(trigger, field, t) ?? '-';

          const value =
            attrLabel === '-' && comparison === '-' && trigger.value === undefined ? (
              t('Not set')
            ) : (
              <>
                {attrLabel} {comparison} {getTriggerValueLabel(trigger, t, store)}
              </>
            );

          return (
            <li key={trigger.key}>
              <Value className={styles.triggerActivity} value={value} />
            </li>
          );
        })}
      </ul>
    );
  },
);

MailrailTriggerSummary.displayName = 'MailrailTriggerSummary';

export default MailrailTriggerSummary;
